import React from 'react';
import { Link } from 'react-router-dom';
import SEO, { softwareAppSchema } from '../../Components/SEO';
import SEOPageLayout from '../../Components/SEOPageLayout';

export default function Features() {
  return (
    <SEOPageLayout>
      <SEO
        title="CheckScript AI Features — AI Assessment Assistant, Remote Desktop & More"
        description="Explore the powerful features of CheckScript AI, including our Advanced stealth capture system MCQ solver, low-latency remote desktop, and offline Neural Driver AI engine."
        keywords="AI assessment tool, AI exam assistant, smart study tool, CheckScript features"
        path="/features"
        structuredData={softwareAppSchema()}
      />

      <section className="seo-hero seo-animate">
        <div className="seo-mesh-bg"></div>
        <div className="seo-container seo-container--sm">
          <div className="seo-hero-badge">⚡ The Ultimate Assessment Toolkit</div>
          <h1 className="seo-h1">
            Everything You Need to <span className="seo-gradient-text">Master Assessments</span>
          </h1>
          <p className="seo-lead">
            CheckScript AI combines advanced computer vision, state-of-the-art local AI inference, and ultra-low latency remote control into a single, seamless platform.
          </p>
          <div className="seo-hero-actions">
            <Link to="/download" className="seo-btn seo-btn--primary seo-btn--lg">Download Free for Windows</Link>
            <Link to="/pricing" className="seo-btn seo-btn--secondary seo-btn--lg">View Pricing Plans</Link>
          </div>
        </div>
      </section>

      <div className="seo-container seo-animate seo-animate-delay-1">
        <div className="seo-stats">
          <div className="seo-stat">
            <div className="seo-stat-value">2,500+</div>
            <div className="seo-stat-label">Active Users</div>
          </div>
          <div className="seo-stat">
            <div className="seo-stat-value">4.8/5</div>
            <div className="seo-stat-label">Average Rating</div>
          </div>
          <div className="seo-stat">
            <div className="seo-stat-value">15+</div>
            <div className="seo-stat-label">Languages Supported</div>
          </div>
          <div className="seo-stat">
            <div className="seo-stat-value">99.9%</div>
            <div className="seo-stat-label">API Uptime</div>
          </div>
        </div>
      </div>

      <section className="seo-section seo-animate seo-animate-delay-2">
        <div className="seo-container">
          <div className="seo-bento-grid">
            
            <Link to="/features/ai-assessment" className="seo-bento-box seo-bento-square" style={{ textDecoration: 'none' }}>
              <div className="seo-card-icon seo-card-icon--cyan">🧠</div>
              <h2 className="seo-h2" style={{ color: 'var(--seo-text)' }}>AI Assessment Assistant</h2>
              <p className="seo-mb-24">
                Our core "Advanced stealth capture system" technology. Effortlessly scan multiple-choice questions, code problems, and complex diagrams using our advanced Tesseract OCR pipeline. Receive high-accuracy solutions directly via customizable hotkeys (Alt+O, Alt+1-4) without disrupting your workflow.
              </p>
              <span className="seo-btn seo-btn--ghost seo-w-full seo-flex-center">Explore AI Assessment →</span>
            </Link>

            <Link to="/features/remote-desktop" className="seo-bento-box seo-bento-square" style={{ textDecoration: 'none' }}>
              <div className="seo-card-icon seo-card-icon--emerald">🖥️</div>
              <h2 className="seo-h2" style={{ color: 'var(--seo-text)' }}>CheckScript Remote</h2>
              <p className="seo-mb-24">
                Experience sub-100ms latency remote access directly from your browser. Powered by Ultra-low latency streaming protocol, Cloudflare edge networks, and H.264 streaming. Get remote support, collaborate on code, or troubleshoot issues without installing heavy viewer software.
              </p>
              <span className="seo-btn seo-btn--ghost seo-w-full seo-flex-center">Explore Remote Desktop →</span>
            </Link>

            <Link to="/features/neural-driver" className="seo-bento-box seo-bento-square" style={{ textDecoration: 'none' }}>
              <div className="seo-card-icon seo-card-icon--amber">🔮</div>
              <h2 className="seo-h2" style={{ color: 'var(--seo-text)' }}>Neural Driver Host</h2>
              <p className="seo-mb-24">
                Run powerful AI models (like Qwen2-VL) entirely offline on your local GPU. Enjoy zero API latency and 100% data privacy. Includes a built-in ChatGPT-style web interface accessible at localhost, perfect for offline study environments.
              </p>
              <span className="seo-btn seo-btn--ghost seo-w-full seo-flex-center">Explore Neural Driver →</span>
            </Link>

            <Link to="/features/screen-capture" className="seo-bento-box seo-bento-square" style={{ textDecoration: 'none' }}>
              <div className="seo-card-icon seo-card-icon--rose">📸</div>
              <h2 className="seo-h2" style={{ color: 'var(--seo-text)' }}>Advanced Screen Capture</h2>
              <p className="seo-mb-24">
                Capture study material from any application, even secure platforms or those with DRM restrictions. CheckScript utilizes 5 fallback capture engines (Hardware-accelerated capture, Magnification API, PrintWindow, Hybrid, GDI) to ensure you always get the shot.
              </p>
              <span className="seo-btn seo-btn--ghost seo-w-full seo-flex-center">Explore Screen Capture →</span>
            </Link>

          </div>
        </div>
      </section>

      <section className="seo-cta seo-animate seo-animate-delay-3">
        <div className="seo-container">
          <div className="seo-cta-box">
            <h2 className="seo-h2">Ready to Upgrade Your Study Workflow?</h2>
            <p className="seo-text-secondary seo-mb-32" style={{ fontSize: '1.1rem' }}>
              Join thousands of students and developers already using CheckScript AI to master their assessments and boost productivity.
            </p>
            <div className="seo-hero-actions">
              <Link to="/download" className="seo-btn seo-btn--primary">Get CheckScript v19.0.0</Link>
              <Link to="/contact" className="seo-btn seo-btn--secondary">Contact Sales</Link>
            </div>
          </div>
        </div>
      </section>
    </SEOPageLayout>
  );
}
